import { StreamDroppedError } from 'features/sentinelDesign/designStream'
import type { ModelSeries } from 'features/timeSeries/agentTab/useAgentModels'

/**
 * The transcript, and how a streamed frame folds into it.
 *
 * Pure on purpose: this is the whole behaviour of the chat panel — which frame
 * becomes which card, and how a tool result finds the call it belongs to — and
 * it used to live in an eighty-line switch inside a fetch callback where none
 * of it could be exercised.
 *
 * The shapes here are also the stored transcript: fim-service folds the same
 * events into the same parts (`sentinel/chat/transcript.py`), so a resumed
 * conversation and a live one are the same document.
 */

/** A tinted span the agent asked the eye to go to. */
export interface ChartBand {
  start: string
  end: string
  label: string
}

/**
 * How an ad-hoc agent pen gets its numbers. `expr` is evaluated per timestamp
 * by the chart's own math; `average` is a trailing window, which an expression
 * cannot do because the evaluator sees one timestamp at a time.
 */
export type AgentPenSpec =
  | { kind: 'expr'; expr: string; tags: Record<string, string> }
  | { kind: 'average'; tag: string; hours: number }

/** The chart move that accompanies a finding. Absent fields are left alone. */
export interface FocusRequest {
  start?: string
  end?: string
  show: string[]
  hide: string[]
  axis?: 'single' | 'multi'
  band?: ChartBand
  note: string
}

export type ChatPart =
  | { kind: 'text'; text: string }
  | {
      kind: 'tool'
      tool: string
      args: Record<string, unknown>
      result?: unknown
    }
  | { kind: 'status'; text: string }
  | {
      kind: 'roles'
      dataset: string
      roles: Record<string, string>
      start?: string
      end?: string
    }
  | { kind: 'kpi'; kpi: string; expr: string }
  | {
      kind: 'fit'
      stats: Record<string, unknown>
      series?: ModelSeries | null
    }
  | { kind: 'saved'; entryId: string; name: string }
  | ({ kind: 'focus' } & FocusRequest)
  | {
      kind: 'pen'
      op: 'add' | 'remove'
      penId: string
      name: string
      unit?: string
      spec?: AgentPenSpec
    }

export interface ChatTurn {
  role: 'user' | 'assistant'
  parts: ChatPart[]
  /** Who typed it. Conversations are shared, so a turn says whose it was. */
  author?: string
}

/** A frame the panel acts on, once it is known to be well formed. */
export type ChatFrame =
  | { kind: 'text' | 'status'; text: string }
  | { kind: 'tool_call'; tool: string; args: Record<string, unknown> }
  | { kind: 'tool_result'; tool: string; result: unknown }
  | {
      kind: 'roles'
      dataset: string
      roles: Record<string, string>
      start?: string
      end?: string
    }
  | { kind: 'kpi'; kpi: string; expr: string }
  | { kind: 'fit'; stats: Record<string, unknown>; series?: ModelSeries }
  | { kind: 'saved'; entryId: string; name: string }
  | ({ kind: 'focus' } & FocusRequest)
  | {
      kind: 'pen'
      op: 'add' | 'remove'
      penId: string
      name: string
      unit?: string
      spec?: AgentPenSpec
    }

const str = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined

const record = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => !!str(item))
    : []

const band = (value: unknown): ChartBand | undefined => {
  const raw = record(value)
  const start = str(raw.start)
  const end = str(raw.end)
  return start && end ? { start, end, label: str(raw.label) ?? '' } : undefined
}

const penSpec = (value: unknown): AgentPenSpec | undefined => {
  const raw = record(value)
  if (raw.kind === 'expr') {
    const expr = str(raw.expr)
    return expr
      ? { kind: 'expr', expr, tags: record(raw.tags) as Record<string, string> }
      : undefined
  }
  if (raw.kind === 'average') {
    const tag = str(raw.tag)
    const hours = typeof raw.hours === 'number' ? raw.hours : 0
    return tag && hours > 0 ? { kind: 'average', tag, hours } : undefined
  }
  return undefined
}

/** Envelope keys the fit's stats must not inherit. */
const ENVELOPE = new Set(['kind', 'type', 'event', 'series'])

/**
 * Read one raw SSE payload, or null when it is not a frame this panel shows.
 * Narrowing here rather than at each branch is what keeps the fold total.
 */
export const parseFrame = (raw: Record<string, unknown>): ChatFrame | null => {
  const kind = raw.kind
  switch (kind) {
    case 'text':
    case 'status': {
      const text = str(raw.text)
      return text === undefined ? null : { kind, text }
    }
    case 'tool_call': {
      const tool = str(raw.tool)
      return tool ? { kind, tool, args: record(raw.args) } : null
    }
    case 'tool_result': {
      const tool = str(raw.tool)
      return tool ? { kind, tool, result: raw.result } : null
    }
    case 'roles': {
      const dataset = str(raw.dataset)
      if (!dataset) return null
      return {
        kind,
        dataset,
        roles: record(raw.roles) as Record<string, string>,
        start: str(raw.start),
        end: str(raw.end),
      }
    }
    case 'kpi': {
      const kpi = str(raw.kpi)
      return kpi ? { kind, kpi, expr: str(raw.expr) ?? '' } : null
    }
    case 'fit': {
      const stats = Object.fromEntries(
        Object.entries(raw).filter(([key]) => !ENVELOPE.has(key))
      )
      return { kind, stats, series: raw.series as ModelSeries | undefined }
    }
    case 'saved': {
      const entryId = str(raw.entry_id) ?? str(raw.entryId)
      return entryId ? { kind, entryId, name: str(raw.name) ?? '' } : null
    }
    case 'focus': {
      const axis =
        raw.axis === 'single' || raw.axis === 'multi' ? raw.axis : undefined
      return {
        kind,
        start: str(raw.start),
        end: str(raw.end),
        show: strings(raw.show),
        hide: strings(raw.hide),
        axis,
        band: band(raw.band),
        note: str(raw.note) ?? '',
      }
    }
    case 'pen': {
      const penId = str(raw.pen_id) ?? str(raw.penId)
      if (!penId) return null
      const op = raw.op === 'remove' ? 'remove' : 'add'
      const spec = penSpec(raw.spec)
      // An add with no readable spec would put a pen on the chart that can
      // never draw, which reads as a broken chart rather than a bad frame.
      if (op === 'add' && !spec) return null
      return {
        kind,
        op,
        penId,
        name: str(raw.name) ?? '',
        unit: str(raw.unit),
        spec,
      }
    }
    default:
      return null
  }
}

/** The part a frame becomes. Tool results edit an existing part instead. */
const partFor = (
  frame: Exclude<ChatFrame, { kind: 'tool_result' }>
): ChatPart => {
  switch (frame.kind) {
    case 'text':
    case 'status':
      return { kind: frame.kind, text: frame.text }
    case 'tool_call':
      return { kind: 'tool', tool: frame.tool, args: frame.args }
    case 'roles':
      return {
        kind: 'roles',
        dataset: frame.dataset,
        roles: frame.roles,
        start: frame.start,
        end: frame.end,
      }
    case 'kpi':
      return { kind: 'kpi', kpi: frame.kpi, expr: frame.expr }
    case 'fit':
      return { kind: 'fit', stats: frame.stats, series: frame.series }
    case 'saved':
      return { kind: 'saved', entryId: frame.entryId, name: frame.name }
    case 'focus':
    case 'pen':
      return frame
  }
}

/**
 * A tool result belongs to the OLDEST call of that tool still waiting for one:
 * calls run in order and results arrive in the same order (FIFO), so matching
 * the newest pending call would hand two in-flight calls of the same tool each
 * other's answers.
 */
const withToolResult = (
  parts: ChatPart[],
  tool: string,
  result: unknown
): ChatPart[] => {
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]
    if (
      part.kind === 'tool' &&
      part.tool === tool &&
      part.result === undefined
    ) {
      const next = [...parts]
      next[i] = { ...part, result }
      return next
    }
  }
  return parts
}

/**
 * Fold a frame into the transcript. Frames only ever extend the trailing
 * assistant turn, so a stream that arrives after the user typed again is
 * dropped rather than rewriting history.
 */
export const foldFrame = (turns: ChatTurn[], frame: ChatFrame): ChatTurn[] => {
  const last = turns[turns.length - 1]
  if (!last || last.role !== 'assistant') return turns
  const parts =
    frame.kind === 'tool_result'
      ? withToolResult(last.parts, frame.tool, frame.result)
      : [...last.parts, partFor(frame)]
  if (parts === last.parts) return turns
  const next = [...turns]
  next[next.length - 1] = { ...last, parts }
  return next
}

/** The words of a turn, which is all a summary or a title needs. */
export const turnText = (turn: ChatTurn): string =>
  turn.parts
    .filter(
      (part): part is Extract<ChatPart, { kind: 'text' }> =>
        part.kind === 'text'
    )
    .map((part) => part.text)
    .join('\n\n')

export const DROPPED_MID_RUN =
  'The connection dropped mid-run. Say “continue” to pick up where it left off — nothing pulled was lost.'

/**
 * What the user reads when a turn fails. A dropped connection is the one
 * failure that is recoverable with a word, because the server keeps the
 * message and persists the partial turn, so it must not read like an outage.
 */
export const failureMessage = (err: unknown): string => {
  if (err instanceof StreamDroppedError) return DROPPED_MID_RUN
  return err instanceof Error ? err.message : String(err)
}
